let MoonfallDiscoveryPalette;

// Deterministic per-card colors for first-discovery chrome. The palette is a
// pure function of the cardId — no cloud, no localStorage, no randomness at
// render time — so the same card shows the same gradient on every surface,
// device, and session, forever. Consumers read the stamped CSS variables
// (--fd-hue-a / --fd-hue-b) and derive gradients with fixed
// saturation/lightness so text contrast stays legible for any hue.
(function initDiscoveryPalette() {
    'use strict';

    // FNV-1a 32-bit: tiny, stable, and well-distributed for short ids.
    function fnv1a(text) {
        let hash = 0x811c9dc5;
        const input = String(text || '');
        for (let i = 0; i < input.length; i += 1) {
            hash ^= input.charCodeAt(i);
            hash = Math.imul(hash, 0x01000193);
        }
        return hash >>> 0;
    }

    function hashedHues(seed) {
        const hash = fnv1a(seed);
        const hueA = hash % 360;
        // Second hue sits 50-310 degrees away so every card reads as a real
        // two-color gradient rather than a flat tint.
        const hueB = (hueA + 50 + ((hash >>> 9) % 260)) % 360;
        // Saturation and lightness are hash-derived too — otherwise every card
        // sits in one muted family and only the hue angle changes. The bands
        // keep light text legible on the darkest and the most vivid draw:
        // saturation 45-85%, lightness 24-42%.
        const satA = 45 + ((hash >>> 13) % 41);
        const satB = 45 + ((hash >>> 17) % 41);
        const lightA = 24 + ((hash >>> 21) % 19);
        const lightB = 24 + ((hash >>> 25) % 19);
        return { hueA, hueB, satA, satB, lightA, lightB };
    }

    function hueDistance(a, b) {
        const diff = Math.abs(a - b) % 360;
        return Math.min(diff, 360 - diff);
    }

    function huesFor(cardId, variant) {
        const normal = hashedHues(cardId);
        if (variant !== 'stellar') return normal;
        // The Stellar first is its own honor: salt the hash for an independent
        // draw, then guarantee it never reads as a re-tint of the same card's
        // normal ribbon — rotate both hues when the anchors land within 60°.
        const stellar = hashedHues(`stellar✦${cardId}`);
        if (hueDistance(stellar.hueA, normal.hueA) < 60) {
            stellar.hueA = (stellar.hueA + 120) % 360;
            stellar.hueB = (stellar.hueB + 120) % 360;
        }
        return stellar;
    }

    const VAR_NAMES = ['hue-a', 'hue-b', 'sat-a', 'sat-b', 'light-a', 'light-b'];

    function applyTo(element, cardId, variant) {
        if (!element?.style) return;
        const { hueA, hueB, satA, satB, lightA, lightB } = huesFor(cardId, variant);
        element.style.setProperty('--fd-hue-a', String(hueA));
        element.style.setProperty('--fd-hue-b', String(hueB));
        element.style.setProperty('--fd-sat-a', `${satA}%`);
        element.style.setProperty('--fd-sat-b', `${satB}%`);
        element.style.setProperty('--fd-light-a', `${lightA}%`);
        element.style.setProperty('--fd-light-b', `${lightB}%`);
    }

    function clearFrom(element) {
        if (!element?.style) return;
        for (const name of VAR_NAMES) element.style.removeProperty(`--fd-${name}`);
    }

    MoonfallDiscoveryPalette = { huesFor, applyTo, clearFrom };
})();

export { MoonfallDiscoveryPalette };
